import { labels } from "@omnia/shared/generated/labels";
import { getCombinedGrassLabel } from "@omnia/shared/grassLabels";
import { hectareFormat, tonnesFormat, tonnesPerHectareFormat } from "@omnia/shared/utils/format";
import { FieldViewModel, IOmniaCommandGroup, IOmniaShell } from "@omnia/types";
import { defineDirective, FieldsMap } from "../../omnia";
import { IFieldsMapCroppingDetailsScope, IFieldsService } from "../../types";

export class CroppingDetailsCtrl {
    static create(): ng.IDirective {
        return defineDirective(CroppingDetailsCtrl, "app/fields/map/croppingDetails/croppingDetails.html");
    }

    map: FieldsMap;

    public selectedCroppings: FieldViewModel[] = [];

    public commands: IOmniaCommandGroup;

    static $inject = ["$scope", "OmniaFieldsService", "OmniaShell"];

    private _managementLabel: string;

    constructor(private $scope: ng.IScope, private svc: IFieldsService, private shell: IOmniaShell) {}

    $onInit() {
        const parentScope = <ng.IScope & IFieldsMapCroppingDetailsScope>this.$scope.$parent;
        const { context, setVisibility } = parentScope;

        this._managementLabel = omnia.crops.getCropManagementConfig(this.shell.getSession()).shortTitle;

        this.commands = context.commands;

        setVisibility(true);

        this.$scope.$watch(
            () => context.getSelectedCroppings(),
            (selectedCroppings) => {
                this.selectedCroppings = selectedCroppings;
            }
        );
    }

    get single(): FieldViewModel | undefined {
        return this.selectedCroppings.length === 1 ? this.selectedCroppings[0] : undefined;
    }

    get totalArea() {
        return this.selectedCroppings.map((x) => x.area).reduce((prev, curr) => prev + (curr ?? 0), 0);
    }

    get totalYield() {
        return this.selectedCroppings
            .map((x) => x.cropping.TargetYield * x.area)
            .reduce((prev, curr) => prev + (curr ?? 0), 0);
    }

    get descriptions() {
        return this.selectedCroppings.map((x) => x.cropDescription).filter(Boolean);
    }

    get plotIds() {
        return this.selectedCroppings
            .map((x) => x.cropping.PlotId)
            .filter(Boolean)
            .sort();
    }

    get batchIds() {
        return this.selectedCroppings
            .map((x) => x.cropping.BatchId)
            .filter(Boolean)
            .sort();
    }

    public purpose(item: FieldViewModel) {
        return item.marketId ? labels.CropPurpose(item.marketId) : undefined;
    }

    public protection(item: FieldViewModel) {
        return item.cropping.ProtectionStatus ? labels.CropProtectionStatus(item.cropping.ProtectionStatus) : undefined;
    }

    public grass(item: FieldViewModel) {
        const m = item.cropping.GrassManagement;
        const c = item.cropping.GrassCuts;
        return getCombinedGrassLabel(m, c);
    }

    public formatAsHectares(value: number) {
        return hectareFormat.format(value);
    }

    public formatAsTonnesPerHectare(value: number) {
        return tonnesPerHectareFormat(value);
    }

    public formatAsTonnes(value: number) {
        return tonnesFormat(value);
    }

    public management(item: FieldViewModel) {
        return {
            label: this._managementLabel,
            options: (item.cropping.Management?.Options ?? []).map((x) => ({
                code: x.Code,
                area: x.Area ?? item.cropping.Area,
            })),
        };
    }
}
